import React, { useState, useEffect } from "react";
import { Box, IconButton, Container } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import FeatureRequestSubmission from "./FeatureRequestSubmission";
import FeatureRequestList from "./FeatureRequestList";
import { User } from "../../types/api";
import { useFeatureRequest } from "../../hooks/useFeatureRequest";
import {
  getStorageWithExpiry,
  setStorageWithExpiry,
} from "../../utils/localStorage";
import { FEATURE_REQUEST_BANNER } from "../../constants";
import { createFeatureRequestBannerStyles } from "./styles";

/**
 * Props for the FeatureRequestBanner component
 */
interface FeatureRequestBannerProps {
  user: User | null;
}

/**
 * FeatureRequestBanner component displays a banner for users to submit and vote on feature requests.
 *
 * The banner includes:
 * - A form for submitting new feature requests
 * - A list of existing feature requests with voting functionality
 * - Persistent visibility state using localStorage
 * - Close functionality with expiry-based hiding
 *
 * The banner is only visible when:
 * - A user is logged in
 * - The banner hasn't been manually closed (or the close state has expired)
 * - Features have been successfully loaded
 *
 * @param props - The component props
 * @param props.user - The current authenticated user, or null if not logged in
 * @returns The FeatureRequestBanner component or null if not visible
 */
const FeatureRequestBanner: React.FC<FeatureRequestBannerProps> = ({
  user,
}) => {
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const theme = useTheme();
  const featureRequestBannerStyles = createFeatureRequestBannerStyles(theme);

  const {
    features,
    isLoading,
    isSubmitting,
    error,
    loadFeatures,
    submitFeature,
    voteForFeature,
    removeVote,
    hasUserVoted,
    addUserVote,
    clearError,
  } = useFeatureRequest();

  /**
   * Initializes the feature request banner when the component mounts or when the user changes.
   *
   * Checks the stored banner state in localStorage to determine if the banner should be shown.
   * If the banner was previously closed and hasn't expired, it remains hidden.
   * Otherwise, it loads the features and shows the banner.
   *
   * @param user._id - The user ID used as a dependency for the effect
   */
  useEffect(() => {
    const initializeBanner = async () => {
      if (user?._id) {
        // Check stored banner state
        const bannerState = getStorageWithExpiry(
          FEATURE_REQUEST_BANNER.BANNER_STATE_KEY
        );

        // If banner was closed and hasn't expired, keep it hidden
        if (bannerState === "closed") {
          setIsVisible(false);
          return;
        }

        // Otherwise show banner and fetch data
        try {
          await loadFeatures(user._id);
          setIsVisible(true);
        } catch (error) {
          console.error("Error loading features:", error);
          setIsVisible(true);
        }
      }
    };

    initializeBanner();
  }, [user?._id]);

  /**
   * Handles submitting a new feature request.
   *
   * Clears any existing errors, submits the feature request, and shows a success tooltip
   * if the submission is successful.
   *
   * @param title - The title of the feature request
   * @param description - The description of the feature request
   * @param userId - The ID of the user submitting the feature request
   */
  const handleSubmitFeature = async (
    title: string,
    description: string,
    userId: string
  ) => {
    clearError();
    try {
      const response = await submitFeature({ title, description }, userId);
      if (response.success) {
        setHasSubmitted(true);
        setTimeout(() => setHasSubmitted(false), 3000);
      }
    } catch (error) {
      console.error("Error submitting feature:", error);
    }
  };

  /**
   * Handles voting for a feature request.
   *
   * Toggles the user's vote for a specific feature. If the user has already voted,
   * it removes the vote. If they haven't voted, it adds a vote and updates the
   * local state and feature list.
   *
   * @param featureId - The ID of the feature to vote for
   * @param userId - The ID of the user casting the vote
   */
  const handleVote = async (featureId: string, userId: string) => {
    try {
      if (hasUserVoted(featureId)) {
        const response = await removeVote(userId, featureId);
        // Features already updated in removeVote function
      } else {
        await voteForFeature(userId, featureId);
        addUserVote(featureId);
        // Fetch updated features
        await loadFeatures(userId);
      }
    } catch (error) {
      console.error("Error handling vote:", error);
    }
  };

  /**
   * Handles closing the banner and storing the closed state in localStorage.
   *
   * Sets the banner as invisible and stores the "closed" state in localStorage
   * with an expiry time defined by FEATURE_REQUEST_BANNER.BANNER_EXPIRY_HOURS.
   */
  const handleClose = () => {
    setIsVisible(false);
    setStorageWithExpiry(
      FEATURE_REQUEST_BANNER.BANNER_STATE_KEY,
      "closed",
      FEATURE_REQUEST_BANNER.BANNER_EXPIRY_HOURS
    );
  };

  if (!isVisible) return null;

  return (
    <Box sx={featureRequestBannerStyles.banner}>
      <Container maxWidth="xl" sx={featureRequestBannerStyles.container}>
        <IconButton
          onClick={handleClose}
          sx={featureRequestBannerStyles.closeButton}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={featureRequestBannerStyles.content}>
          <FeatureRequestSubmission
            onSubmit={handleSubmitFeature}
            isSubmitting={isSubmitting}
            hasSubmitted={hasSubmitted}
            userId={user?._id || ""}
            error={error}
          />
          <FeatureRequestList
            features={features}
            onVote={handleVote}
            hasUserVoted={hasUserVoted}
            userId={user?._id || ""}
            shuffle={FEATURE_REQUEST_BANNER.SHUFFLE_FEATURES}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default FeatureRequestBanner;
